import { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, Search, Star, Sparkles, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useLocation } from '../contexts/LocationContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventSkeleton = ({ featured = false }) => (
  <div className={`overflow-hidden rounded-xl border border-gray-200 animate-pulse ${featured ? 'h-80' : ''}`}>
    <div className={`bg-gray-200 ${featured ? 'h-56' : 'h-48 md:h-40'}`} />
    <div className="p-5 space-y-3">
      <div className="h-4 bg-gray-200 rounded w-1/3" />
      <div className="h-5 bg-gray-200 rounded w-3/4" />
      <div className="h-4 bg-gray-200 rounded w-full" />
      <div className="h-4 bg-gray-200 rounded w-1/2" />
    </div>
  </div>
);

const Events = () => {
  const { locationString } = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [allEvents, setAllEvents] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  const fetchEvents = async (loc, cat) => {
    if (!loc) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/events/ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location: loc, category: cat || 'all' }),
      });
      if (!res.ok) throw new Error('Failed to fetch events');
      const data = await res.json();
      setAllEvents(data.events || []);
      setFeaturedEvents(data.featured || (data.events || []).filter(e => e.featured).slice(0, 2));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (locationString) fetchEvents(locationString, category);
  }, [locationString]); // eslint-disable-line

  const handleCategoryChange = (val) => {
    setCategory(val);
    if (locationString) fetchEvents(locationString, val);
  };

  const filteredEvents = allEvents.filter(e => {
    const q = searchQuery.toLowerCase();
    return !q || e.title?.toLowerCase().includes(q) || e.location?.toLowerCase().includes(q);
  });

  const nonFeatured = filteredEvents.filter(e => !featuredEvents.some(f => f.id === e.id));

  const handleBookEvent = (event) => {
    toast({ title: 'Booking Initiated!', description: `Proceeding to book tickets for ${event.title}` });
  };

  const formatDate = (ds) => {
    if (!ds) return '';
    try {
      return new Date(ds).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return ds; }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#F4C430] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2">Food Events & Experiences</h1>
            <p className="text-white/90 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              AI-curated{locationString ? ` in ${locationString}` : ' — set your location to personalise'}
            </p>
          </div>
          {locationString && (
            <Button variant="ghost" onClick={() => fetchEvents(locationString, category)} disabled={loading} className="text-white hover:bg-white/20">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* No location */}
        {!locationString && (
          <div className="text-center py-16">
            <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Set your location first</h3>
            <p className="text-gray-500">Use the location prompt to find food events near you</p>
          </div>
        )}

        {locationString && (
          <>
            {/* Search & Filters */}
            <div className="bg-white rounded-xl shadow-sm p-5 mb-8 flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input placeholder="Search events..." className="pl-10 h-11" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </div>
              <Select value={category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-full md:w-52 h-11">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="Festival">Festivals</SelectItem>
                  <SelectItem value="Workshop">Workshops</SelectItem>
                  <SelectItem value="Market">Markets</SelectItem>
                  <SelectItem value="Tasting">Tastings</SelectItem>
                  <SelectItem value="Popup">Pop-ups</SelectItem>
                  <SelectItem value="Competition">Competitions</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-3 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />{error} — <button onClick={() => fetchEvents(locationString, category)} className="underline">Retry</button>
              </div>
            )}

            {/* Featured Events */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-5">Featured Events</h2>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <EventSkeleton featured /><EventSkeleton featured />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(searchQuery ? filteredEvents.slice(0, 2) : featuredEvents).map((event, i) => (
                    <Card key={event.id || i} className="overflow-hidden hover-lift border border-gray-200 hover:border-[#FF6B35] transition-all">
                      <div className="relative h-56">
                        <img loading="lazy" src={event.image} alt={event.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <Badge className="absolute top-4 right-4 bg-[#F4C430] text-gray-900 font-semibold">
                          <Star className="w-3 h-3 mr-1" />Featured
                        </Badge>
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <Badge variant="outline" className="mb-2 border-white/60 text-white text-xs">{event.category}</Badge>
                          <h3 className="text-2xl font-bold leading-tight">{event.title}</h3>
                        </div>
                      </div>
                      <CardContent className="p-6">
                        <p className="text-gray-600 mb-4 line-clamp-2">{event.description}</p>
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-sm text-gray-600"><Calendar className="w-4 h-4 mr-2 text-[#FF6B35]" />{formatDate(event.date)} at {event.time}</div>
                          <div className="flex items-center text-sm text-gray-600"><MapPin className="w-4 h-4 mr-2 text-[#FF6B35]" />{event.location}</div>
                          <div className="flex items-center text-sm text-gray-600"><Users className="w-4 h-4 mr-2 text-[#FF6B35]" />{(event.attendees || 0).toLocaleString()} expected</div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-2xl font-bold text-[#FF6B35]">{event.price === 0 ? 'Free' : `$${event.price}`}</div>
                          <Button className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white" onClick={() => handleBookEvent(event)}>Book Now</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* All Other Events */}
            {!loading && nonFeatured.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-5">All Events</h2>
                <div className="space-y-4">
                  {nonFeatured.map((event, i) => (
                    <Card key={event.id || i} className="overflow-hidden hover:border-[#FF6B35] transition-all">
                      <div className="md:flex">
                        <div className="md:w-1/3 relative h-48 md:h-auto flex-shrink-0">
                          <img loading="lazy" src={event.image} alt={event.title} className="w-full h-full object-cover" />
                        </div>
                        <CardContent className="md:w-2/3 p-6">
                          <div className="flex justify-between items-start mb-2">
                            <Badge className="bg-[#FFF5F0] text-[#FF6B35] hover:bg-[#F4C430] hover:text-white">{event.category}</Badge>
                            <div className="text-2xl font-bold text-[#FF6B35]">{event.price === 0 ? 'Free' : `$${event.price}`}</div>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
                          <p className="text-gray-600 mb-4 line-clamp-2">{event.description}</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                            <div className="flex items-center text-sm text-gray-600"><Calendar className="w-4 h-4 mr-2 text-[#FF6B35]" />{formatDate(event.date)}</div>
                            <div className="flex items-center text-sm text-gray-600"><MapPin className="w-4 h-4 mr-2 text-[#FF6B35]" />{event.location}</div>
                          </div>
                          <Button className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white w-full md:w-auto" onClick={() => handleBookEvent(event)}>
                            Book Tickets
                          </Button>
                        </CardContent>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {!loading && filteredEvents.length === 0 && allEvents.length > 0 && (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No events match your search</h3>
                <p className="text-gray-500">Try a different search term or category</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Events;
